import { Dialog } from '@progress/kendo-react-dialogs';
import { EquipmentSelect } from 'app/components/access-sharing';
import { ErrorModal } from 'app/components/ErrorModal';
import { FleetNameTextField } from './components/FleetNameTextField';
import ill_warning from 'assets/images/ill_warning.svg';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import './CreateFleetView.less';
import { SubmitButton } from 'app/components/form-fields';
import {
  BackActionContentLayout,
  CardContentLayout,
} from 'app/components/layouts';
import { sharesApi } from 'services/api/shares';
import { InputHandle } from '@progress/kendo-react-inputs';

interface Props {
  equipment: IEquipmentEntry[];
  selectedFleetName?: string;
  selectedEquipmentIds: Set<string>;
  setSelectedFleetName: (name: string) => void;
  setSelectedFleetId: (id: string) => void;
  setSelectedEquipmentIds: (ids: Set<string>) => void;
  onBack: () => void;
  onNextStep: () => void;
  title?: string;
  subtitle?: string;
}

// TODO Where do they come from? Is there a way to include it into IEquipmentEntry?
const sharedIds = new Set(['0650d2d7-840c-4f78-b26a-69a16af4263c']);
const inFleetIds = new Set(['0bc5a412-1b21-41f8-a849-761f5633d302']);

export default function CreateFleetView({
  equipment,
  title,
  subtitle,
  selectedFleetName,
  selectedEquipmentIds,
  setSelectedFleetName,
  setSelectedFleetId,
  setSelectedEquipmentIds,
  onBack,
  onNextStep,
}: Props) {
  const { t } = useTranslation();

  const [
    isSelectAtLeastOneItemDialogOpen,
    setIsSelectAtLeastOneItemDialogOpen,
  ] = useState(false);
  // TODO Replace with RHF or rework the form validation approach
  const nameRef = useRef<InputHandle>(null);
  const [fleetNameError, setFleetNameError] = React.useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [createFleet] = sharesApi.useCreateFleetMutation();
  const isAllEquipmentDisabled = useMemo(
    () =>
      equipment.every(
        equipment =>
          sharedIds.has(equipment.id) || inFleetIds.has(equipment.id),
      ),
    [equipment],
  );

  async function handleNextStep() {
    if (!selectedFleetName) {
      setFleetNameError(
        t('access_sharing.dialogs.create_fleet.fleet_name_is_required'),
      );
      nameRef.current?.focus();
      return;
    }

    if (!selectedEquipmentIds.size) {
      setIsSelectAtLeastOneItemDialogOpen(true);
      return;
    }

    try {
      const response = await createFleet({
        name: selectedFleetName,
        exposedEquipmentIds: Array.from(selectedEquipmentIds),
      }).unwrap();

      setSelectedFleetId(response.exposedFleetId);
      onNextStep();
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <>
      <BackActionContentLayout onBack={onBack}>
        <CardContentLayout
          title={title ?? t('create_fleet')}
          subtitle={subtitle ?? t('create_fleet_subtitle')}
        >
          <FleetNameTextField
            ref={nameRef}
            selectedFleetName={selectedFleetName}
            isAllEquipmentDisabled={isAllEquipmentDisabled}
            fleetNameError={fleetNameError}
            className="create-fleet-view__fleet-name-text-field"
            setSelectedFleetName={setSelectedFleetName}
            clearFieldError={() => setFleetNameError('')}
          />

          <EquipmentSelect
            equipment={equipment}
            selectedEquipmentIds={selectedEquipmentIds}
            setSelectedEquipmentIds={setSelectedEquipmentIds}
            setIsSubmitDisabled={setIsSubmitDisabled}
            type="fleet"
          />

          {!isAllEquipmentDisabled && (
            <SubmitButton disabled={isSubmitDisabled} onClick={handleNextStep}>
              <div className="create-fleet-view__continue-btn-content">
                {!selectedEquipmentIds.size ? (
                  t('save_fleet_and_continue')
                ) : (
                  <>
                    {t('save_fleet_and_continue')}

                    <span className="create-fleet-view__equipment-selected-text">
                      {t('no_equipment_selected', {
                        no: selectedEquipmentIds.size,
                      })}
                    </span>
                  </>
                )}
              </div>
            </SubmitButton>
          )}
        </CardContentLayout>
      </BackActionContentLayout>

      {isSelectAtLeastOneItemDialogOpen && (
        <Dialog className="modal-center modal-error modal-remove-item modal-fulltext">
          <ErrorModal
            icon={ill_warning}
            title={t('select_at_least_1_item')}
            class="select-items-modal"
            onClose={() => setIsSelectAtLeastOneItemDialogOpen(false)}
          />
        </Dialog>
      )}
    </>
  );
}
